import React, { useState } from 'react'
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Stack,
  Alert,
  LinearProgress,
  InputAdornment,
  IconButton,
} from '@mui/material'
import { Visibility, VisibilityOff, CheckCircle, Cancel, Warning } from '@mui/icons-material'

// パスワード強度評価の型定義
interface PasswordStrength {
  score: number // 0-4
  label: string
  color: 'error' | 'warning' | 'info' | 'success'
  suggestions: string[]
}

// パスワード変更フォームのprops
interface PasswordChangeFormProps {
  onSubmit: (currentPassword: string, newPassword: string) => Promise<void>
  loading?: boolean
  error?: string
  isFirstLogin?: boolean
}

// パスワード強度を評価する関数
const evaluatePasswordStrength = (password: string): PasswordStrength => {
  let score = 0
  const suggestions: string[] = []

  // 長さチェック
  if (password.length >= 8) {
    score += 1
  } else {
    suggestions.push('8文字以上で入力してください')
  }

  // 大文字小文字チェック
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) {
    score += 1
  } else {
    suggestions.push('大文字と小文字を含めてください')
  }

  // 数字チェック
  if (/\d/.test(password)) {
    score += 1
  } else {
    suggestions.push('数字を含めてください')
  }

  // 特殊文字チェック
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    score += 1
  } else {
    suggestions.push('記号を含めてください')
  }

  // スコアに基づくラベルと色の決定
  let label: string
  let color: 'error' | 'warning' | 'info' | 'success'

  if (score <= 1) {
    label = '弱い'
    color = 'error'
  } else if (score === 2) {
    label = '普通'
    color = 'warning'
  } else if (score === 3) {
    label = '強い'
    color = 'info'
  } else {
    label = '非常に強い'
    color = 'success'
  }

  return { score, label, color, suggestions }
}

const PasswordChangeForm: React.FC<PasswordChangeFormProps> = ({
  onSubmit,
  loading = false,
  error,
  isFirstLogin = false,
}) => {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({})

  // パスワード強度の評価
  const passwordStrength = evaluatePasswordStrength(newPassword)

  // バリデーション関数
  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {}

    // 現在のパスワードチェック
    if (!currentPassword.trim()) {
      errors.currentPassword = isFirstLogin
        ? '仮パスワードを入力してください'
        : '現在のパスワードを入力してください'
    }

    // 新しいパスワードチェック
    if (!newPassword.trim()) {
      errors.newPassword = '新しいパスワードを入力してください'
    } else if (passwordStrength.score < 2) {
      errors.newPassword = 'より強固なパスワードを設定してください'
    }

    // 確認パスワードチェック
    if (!confirmPassword.trim()) {
      errors.confirmPassword = 'パスワードの確認を入力してください'
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'パスワードが一致しません'
    }

    // 同じパスワードのチェック
    if (currentPassword && newPassword && currentPassword === newPassword) {
      errors.newPassword = '現在のパスワードとは異なるパスワードを設定してください'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  // フォーム送信ハンドラー
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      await onSubmit(currentPassword, newPassword)
    } catch (error) {
      // エラーは親コンポーネントで処理
      console.error('Password change failed:', error)
    }
  }

  // パスワード強度インジケーターの色
  const getStrengthColor = (score: number) => {
    if (score <= 1) return '#6B7280' // text.secondary equivalent
    if (score === 2) return '#2C6EBE' // secondary
    if (score === 3) return '#2C6EBE' // secondary
    return '#1F3A5F' // primary
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        maxWidth: 500,
        mx: 'auto',
        mt: { xs: 2, md: 4 },
      }}
    >
      <Stack spacing={3}>
        {/* ヘッダー */}
        <Box textAlign="center">
          <Typography variant="h5" component="h1" gutterBottom>
            {isFirstLogin ? '初回パスワード設定' : 'パスワード変更'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isFirstLogin
              ? 'セキュリティのため、初回ログイン時にパスワードを変更してください'
              : '安全なパスワードに変更してください'}
          </Typography>
        </Box>

        {/* エラーメッセージ */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* フォーム */}
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={3}>
            {/* 現在のパスワード */}
            <TextField
              fullWidth
              label={isFirstLogin ? '仮パスワード' : '現在のパスワード'}
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              error={!!validationErrors.currentPassword}
              helperText={validationErrors.currentPassword}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* 新しいパスワード */}
            <Box>
              <TextField
                fullWidth
                label="新しいパスワード"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                error={!!validationErrors.newPassword}
                helperText={validationErrors.newPassword}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* パスワード強度インジケーター */}
              {newPassword && (
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      パスワード強度:
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: getStrengthColor(passwordStrength.score), fontWeight: 'bold' }}
                    >
                      {passwordStrength.label}
                    </Typography>
                    {passwordStrength.score >= 3 ? (
                      <CheckCircle sx={{ fontSize: 16, color: 'primary.main' }} />
                    ) : passwordStrength.score >= 2 ? (
                      <Warning sx={{ fontSize: 16, color: 'secondary.main' }} />
                    ) : (
                      <Cancel sx={{ fontSize: 16, color: 'text.secondary' }} />
                    )}
                  </Box>

                  <LinearProgress
                    variant="determinate"
                    value={(passwordStrength.score / 4) * 100}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'grey.300',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getStrengthColor(passwordStrength.score),
                        borderRadius: 3,
                      },
                    }}
                  />

                  {passwordStrength.suggestions.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        改善点:
                      </Typography>
                      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                        {passwordStrength.suggestions.map((suggestion, index) => (
                          <Typography
                            key={index}
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.75rem' }}
                          >
                            • {suggestion}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* パスワード確認 */}
            <TextField
              fullWidth
              label="新しいパスワード（確認）"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={!!validationErrors.confirmPassword}
              helperText={validationErrors.confirmPassword}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* 送信ボタン */}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ mt: 3, py: 1.5 }}
            >
              {loading ? 'パスワード変更中...' : 'パスワードを変更'}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  )
}

export default PasswordChangeForm
